import BpkCard from '@skyscanner-internal/backpack-web/bpk-component-card';
import {
  withLazyLoading,
  withLoadingBehavior,
} from '@skyscanner-internal/backpack-web/bpk-component-image';
import {
  BpkFlex,
  BpkVessel,
} from '@skyscanner-internal/backpack-web/bpk-component-layout';
import BpkText, {
  TEXT_COLORS,
  TEXT_STYLES,
} from '@skyscanner-internal/backpack-web/bpk-component-text';

import BackgroundImage from '../BackgroundImage';
import DirectFlightLabel from '../DirectFlightLabel';
import QuoteLink from '../QuoteLink';
import { DEFAULT_IMAGE_URL, DIRECTNESS } from '../constants';
import { buildBrowseViewLink } from '../helpers/url-builder';
import { trackSelectResult } from '../services/event-tracking';

import type { BrowseListCardProps } from '@skyscanner-internal/landing-pages-shared-types/types-shared/DestinationsExplorer';

import STYLES from './BrowseListCard.module.scss';

const PRICED = 'priced';
const UNPRICED = 'unpriced';
const AVAILABLE = 'available';
const UNAVAILABLE = 'unavailable';
const RESIZE_PARAMETERS = '?resize=500px:500px&quality=60';

const documentIfExists = typeof window !== 'undefined' ? document : null;

const clickHandler = (
  e: Event,
  browseViewLink: string,
  props: BrowseListCardProps,
) => {
  e.preventDefault();

  const {
    place: { destinationId, direct, imageUrl, price },
    position,
    query,
  } = props;

  const priced = price ? PRICED : UNPRICED;
  const directStr = DIRECTNESS[direct] || '';
  const imageAvailable =
    imageUrl === DEFAULT_IMAGE_URL ? UNAVAILABLE : AVAILABLE;

  trackSelectResult({
    ...query,
    position,
    direct: directStr.toLowerCase(),
    priced,
    imageAvailable,
    placeCode: destinationId,
  });

  window.location.href = browseViewLink;
};

const LoadedBackgroundImage = (params: any) => (
  <BackgroundImage {...params} inView loading={false} />
);

const BrowseListCard = (props: BrowseListCardProps) => {
  const {
    enableLazyLoading = true,
    place: {
      countryName,
      destinationId,
      direct,
      imageUrl,
      inboundDate,
      name,
      outboundDate,
      price,
    },
    query: { origin },
  } = props;

  const browseViewLink = buildBrowseViewLink(
    origin,
    destinationId,
    direct,
    outboundDate,
    inboundDate,
  );

  const Image = enableLazyLoading
    ? withLoadingBehavior(withLazyLoading(BackgroundImage, documentIfExists))
    : withLoadingBehavior(LoadedBackgroundImage);

  return (
    <BpkCard
      className={STYLES.BrowseListCard__card}
      padded={false}
      href={browseViewLink}
      onClick={(e: Event) => clickHandler(e, browseViewLink, props)}
      rel="nofollow"
    >
      <Image src={imageUrl + RESIZE_PARAMETERS} />
      {/* BpkVessel (not BpkFlex): this row needs `gap: 6px` on the same element as
          `display: flex`, and BpkFlex's gap only accepts BpkSpacing tokens (no 6px),
          while BpkFlex also blocks `className`. See
          backpack-api-gaps/WOM-6797-browse-list-card-grid.md. */}
      <BpkVessel className={STYLES.BrowseListCard__container}>
        <BpkFlex direction="column">
          <BpkText
            color={TEXT_COLORS.textPrimary}
            textStyle={TEXT_STYLES.heading4}
            data-testid="browse-list-card-title"
          >
            {name}
          </BpkText>
          {countryName && (
            <BpkText
              color={TEXT_COLORS.textSecondary}
              textStyle={TEXT_STYLES.caption}
              data-testid="browse-list-card-subtitle"
            >
              {countryName}
            </BpkText>
          )}
        </BpkFlex>
        <BpkFlex direction="column" align="flex-end">
          <DirectFlightLabel direct={direct} />
          <QuoteLink price={price} />
        </BpkFlex>
      </BpkVessel>
    </BpkCard>
  );
};

export default BrowseListCard;
